import { appFeatures } from "AppFeatures"
import { l10n } from "store/lang/L10n"

const formatterCache = new Map()

function getFormatter(IntlClass, locale, options = {}, cache = formatterCache) {
	const key = `${IntlClass.name}:${locale}:${JSON.stringify(options)}`
 
	if (!cache.has(key)) {
	  cache.set(key, new IntlClass(locale, options))
	}
	return cache.get(key)
 }

export const getDateFormatter =
	(locale, options) => getFormatter(Intl.DateTimeFormat, locale, options)

export const getDurationFormatter =
	(locale, options) => getFormatter(Intl.DurationFormat, locale, options)

export const getNumberFormatter =
	(locale, options) => getFormatter(Intl.NumberFormat, locale, options)

export const getRelativeTimeFormatter =
	(locale, options) => getFormatter(Intl.RelativeTimeFormat, locale, options)

export const getCountryNamesFormatter =
	(locale) => getFormatter(Intl.DisplayNames, locale, { type: "region" })

export const getLanguageNamesFormatter =
	(locale) => getFormatter(Intl.DisplayNames, locale, { type: "language" })


// mimics / replaces moment formats, since moment locales are very tricky
export function formatDate(date, fmt, locale = appFeatures.locale, timeZone = "Europe/Zurich") {
	if (locale === "gsw") locale = "de"
	const d = new Date(date /*+ 4 * 30 * 86400 * 1000*/) // +x months for testing

	// TOKEN DETECTION (order matters!)
	const has = token => fmt.includes(token)

	const options = {
		timeZone,

		// weekday tokens
		weekday: has("dddd")
			? "long"
			: has("ddd") || has("dd")
			? "short"
			: undefined,

		// year tokens
		year: has("YYYY") ? "numeric" : undefined,

		// month tokens — IMPORTANT: order
		month: has("MMMM")
			? "long"         // e.g. "December" / "Dezember"
			: has("MMM")
			? "short"        // e.g. "Dec" / "Dez"
			: has("MM")
			? "2-digit"      // e.g. "12"
			: undefined,

		// day tokens — D must be checked after DD
		// day tokens — FIX: ensure "DD" check isn't spoofed by the "D" inside MMMM
		//day: has("DD")
		day: (has("DD") && !has("MMMM"))
			? "2-digit"
			: has("D")
			? "numeric"
			: undefined,

		hour: has("HH") ? "2-digit" : undefined,
		minute: has("mm") ? "2-digit" : undefined,
		hour12: false
	}

	const dtf = getDateFormatter(locale, options)

	// formatToParts → { type → value }
	const parts = Object.fromEntries(dtf.formatToParts(d).map(p => [p.type, p.value]))

	// Strip leading zero for "D"
	// MTVW-1147: in German and Italian, the day number is often followed by a dot (e.g. "1." instead of "1"), so we need to keep it in that case
	let numericDay = parts.day ? parts.day.replace(/^0/, "") : undefined
	let fixedDay = parts.day
	if (numericDay && (locale === "de")) {
		numericDay = numericDay.endsWith(".") ? numericDay : numericDay + "."
		fixedDay = fixedDay.endsWith(".") ? fixedDay : fixedDay + ".";
	}
	//console.debug("formatDate", { date, fmt, locale, options, parts, numericDay })

	// REPLACEMENT ORDER: longest → shortest
	const result = fmt
		// weekday
		.replace(/dddd/g, parts.weekday ?? "")
		.replace(/ddd/g, parts.weekday ?? "")
		// MTVW-1147: for "dd" we want to keep the dot in German and Italian (e.g. "1." instead of "1"), but in French we want to remove it and cap it to 2 letters
		.replace(
			/dd/g,
			parts.weekday
			  ? locale === "fr"
				 ? `${parts.weekday.replace(/\.$/, "").slice(0, 2)}.`
				 : locale === "en"
					? parts.weekday.slice(0, 2)
					: parts.weekday + (locale === "de" || locale === "it" ? "." : "")
			  : ""
		)
		// year
		.replace(/YYYY/g, parts.year ?? "")

		// month
		.replace(/MMMM/g, parts.month ?? "")
		.replace(
			/MMM/g,
			parts.month
			  ? parts.month + (locale === "it" ? "." : "")
			  : ""
		 )
		.replace(/MM/g, parts.month ?? "")

		// day
		.replace(/DD/g, locale === "de" ? (fixedDay ?? "") : (parts.day ?? ""))
		.replace(/\bD\b/g, numericDay ?? "") // Triggers your MTVW-1147 dot logic!

		// time
		.replace(/HH/g, parts.hour ?? "")
		.replace(/mm/g, parts.minute ?? "")
		// fixup if we end up with two dots (e.g. 'Di..')
		.replace(/\.\./g, ".")
	
		//console.debug("formatDate", { result, date, fmt, locale, options, parts })
		return result
}

export function formatDuration(hours, minutes, locale = appFeatures.locale) {
	// 1. Native Intl.DurationFormat (if available)
	/*
	const options = {}
	if (typeof Intl.DurationFormat === "function") {
		const df = getDurationFormatter(locale, {
			style: "short",
			hours: "numeric",
			minutes: "numeric",
		})

		return df.format({ hours, minutes })
	}
	*/

	// 2. FALLBACK: small manual formatter
	const fallbackUnits = {
		"de": { hour: "Std.", minute: "Min." },
		"de-CH": { hour: "Std.", minute: "Min." },
		"gsw": { hour: "Std.", minute: "Min." },
		"it": { hour: "h", minute: "min" },
		"fr": { hour: "h", minute: "min" },
		"en": { hour: "h", minute: "m" }
	}

	const units = fallbackUnits[locale] || fallbackUnits["de"]

	const parts = []
	// MTVW-1149: no space between number and unit for English (e.g. "1h 30m" instead of "1 h 30 m")
	if (hours > 0) locale === "en" ? parts.push(`${hours}${units.hour}`) : parts.push(`${hours} ${units.hour}`)
	if (minutes > 0) locale === "en" ? parts.push(`${minutes}${units.minute}`) : parts.push(`${minutes} ${units.minute}`)

	return parts.join(" ")
}

export function minutesShortText(locale = appFeatures.locale) {
	const _minutes = {
		"de": "Min.",
		"de-CH": "Min.",
		"gsw": "Min.",
		"it": "min",
		"fr": "min",
		"en": "m"
	}
	const value = _minutes[locale] || _minutes["de"]
	return value
}

export function formatMinutesLong(minutes, locale = appFeatures.locale) {
	const _minutes = {
		"de": "Minuten",
		"de-CH": "Minuten",
		"gsw": "Minuten",
		"it": "minuti",
		"fr": "minutes",
		"en": "minutes"
	}
	const value = `${minutes} ${_minutes[locale] || _minutes["de"]}`
	return value
}

export function countryName(country, locale = appFeatures.locale) {
	// use l10n instead
	//const cf = getCountryNamesFormatter(locale)
	let _country = country // preset in case of error

	try {
		// use l10n instead
		//_country = cf.of(country)
		let [first, ...rest] = country.toLowerCase()
		// need uppercase first letter to match l10n camel case keys
		_country = first.toUpperCase() + rest.join('')
		_country = l10n["country" + _country]
	}
	catch (e) {
	}
	return _country
}

export function languageName(lang, locale = appFeatures.locale) {
	// use l10n instead
	//const lf = getLanguageNamesFormatter(locale)

	let language = lang // preset in case of error
	try {
		// use l10n instead
		//language = lf.of(lang)
		let [first, ...rest] = lang.toLowerCase()
		// need uppercase first letter to match camel case l10n keys
		lang = first.toUpperCase() + rest.join('')
		language = l10n["language" + lang]
	}
	catch (e) {
		// happens when playing with active French language called from _translate(item) in PlayerTrackVjs
	}
	return language
}

/*
console.debug("formatDate", formatDate(1764852217000, 'D MMM'))
console.debug("formatDate", formatDate(1764852217000, 'ddd DD MMMM', "de"))
console.debug("formatDate", formatDate(1789440769000, 'ddd DD MMMM', "de"))
console.debug("formatDate", formatDate(1764852217000, 'ddd DD MMMM', "fr"))
console.debug("formatDate", formatDate(1789440769000, 'ddd DD MMMM', "fr"))
console.debug("formatDate", formatDate(1764852217000, 'ddd DD MMMM', "it"))
console.debug("formatDate", formatDate(1789440769000, 'ddd DD MMMM', "it"))
console.debug("formatDate", formatDate(1764852217000, 'ddd DD MMMM', "en"))
console.debug("formatDate", formatDate(1789440769000, 'ddd DD MMMM', "en"))

console.debug("formatDate", formatDate(1764852217000, `dd DD.MM.YYYY, HH:mm`, "de"))
console.debug("formatDate", formatDate(1764852217000, `dd DD.MM.YYYY, HH:mm`, "en"))
console.debug("formatDate", formatDate(1764852217000, `dd DD.MM.YYYY, HH:mm`, "fr"))
console.debug("formatDate", formatDate(1764852217000, `dd DD.MM.YYYY, HH:mm`, "it"))

console.debug("formatDate_", formatDate(1764852217000, `dd, DD.MM., HH:mm`, "it"))

console.debug("formatDate", formatDuration(2, 45, "de"))
console.debug("formatDate", formatDuration(2, 45, "en"))
console.debug("formatDate", formatDuration(2, 45, "fr"))
console.debug("formatDate", formatDuration(2, 45, "it"))

console.debug("formatDate", formatDuration(0, 45, "de"))
console.debug("formatDate", formatDuration(0, 45, "en"))
console.debug("formatDate", formatDuration(0, 45, "fr"))
console.debug("formatDate", formatDuration(0, 45, "it"))

console.debug("formatDate", countryName("US", "de"))
console.debug("formatDate", countryName("US", "en"))
console.debug("formatDate", countryName("US", "fr"))
console.debug("formatDate", countryName("US", "it"))

console.debug("formatDate", languageName("en", "de"))
console.debug("formatDate", languageName("en", "en"))
console.debug("formatDate", languageName("en", "fr"))
console.debug("formatDate", languageName("en", "it"))

console.debug("formatDate", formatMinutesLong(110, "de"))
console.debug("formatDate", formatMinutesLong(111, "en"))
console.debug("formatDate", formatMinutesLong(112, "fr"))
console.debug("formatDate", formatMinutesLong(113, "it"))
*/
